import { Sprite, type FederatedPointerEvent } from 'pixi.js';
import { toast, notifyOnce } from '../../dom';
import { isFenceKind } from '../../map/fence-connect';
import { playSfx } from '../../audio/audio';
import { fieldApi } from '../../game-api';
import { TILE, type Decor } from '../../map/world';
import { FOOT_OFFSET, type MoveType } from '../../map/player';
import { actionErrorMessage, FOLIAGE_KINDS, GATHER_KINDS, BUILDINGS, TALL_KINDS, bkey, isCleared, isGathered, isMined, liveRemaining, plotAt, buildingAt, guard, syncHeld, type FarmCtx, refreshBag, dirToTile, setFarm,} from './context';
import { buildCollision, mountVillagers, nearestOpenTile, tileBlocked, walkTo } from './scene';
import { buildBlocker, insideReservedSpace, plotsDestroyedBy, TENT_KIND } from './placement';
import { tentApi } from './building-menu';
import { hideBuildGhost, updateBuildGhost } from './build-ghost';
import { actOnPlot, paintPlots } from './plots';
import { confirmDemolish, demolish } from './demolish';
import { confirmPlotLoss } from './plot-loss';
import { affordEnergy, attackAnimal, attackMob, cancelCut, gatherFoliage, plantAcornAt, startCut } from './resources';
import { castAt, castBlocker } from './fishing';
import { PAINT_GESTURE_MAX, PAINT_PACE_MS, affordBlocker, paintStart, planPaint, strokeTiles, type PaintKind } from './paint-run';
import { paintChrome, paintHotbar } from './hud';
import { toolCursorUrl, waterCursorUrl, type ToolId } from '../../tool-config';
import { dispatchAction, isBusy, isDropped } from '../../action/dispatch';

// ─── Movement input: WASD / arrows (hold) + click-to-walk ─────────────────────
// Keyed by e.code (PHYSICAL key) not e.key, so it works on any layout — a
// Russian keyboard reports е.key='ц' for the W key but е.code='KeyW'.
const MOVE_KEYS = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

export function pushKeys(c: FarmCtx) {
  let vx = 0, vy = 0;
  if (c.keys.has('KeyA') || c.keys.has('ArrowLeft')) vx -= 1;
  if (c.keys.has('KeyD') || c.keys.has('ArrowRight')) vx += 1;
  if (c.keys.has('KeyW') || c.keys.has('ArrowUp')) vy -= 1;
  if (c.keys.has('KeyS') || c.keys.has('ArrowDown')) vy += 1;
  c.player?.setKeys(vx, vy);
}

// Space = jump, Z = roll — one-shot moves facing the hero's current direction.
const MOVE_ONESHOT: Record<string, MoveType> = { Space: 'jump', KeyZ: 'roll' };
const RUN_KEYS = new Set(['ShiftLeft', 'ShiftRight']);

/**
 * Mashing a one-shot key does nothing extra.
 *
 * Tapping E twenty times used to mean twenty mount toggles and twenty toasts —
 * the game answering a question the player only asked once. A short per-key
 * cooldown makes the second through twentieth press a no-op, which is what the
 * player already assumes is happening.
 */
const KEY_COOLDOWN_MS = 300;
const lastKeyAt = new Map<string, number>();
function keyOnCooldown(code: string, now = Date.now()): boolean {
  const prev = lastKeyAt.get(code);
  if (prev !== undefined && now - prev < KEY_COOLDOWN_MS) return true;
  lastKeyAt.set(code, now);
  return false;
}

/**
 * Why a one-shot move (Space = jump, Z = roll) is refused, or null when it may run.
 *
 * A ridden hero has no jump and no roll: those animation rows are the ON-FOOT
 * ones, so firing them while seated popped the hero out of the saddle art for
 * half a second and dashed him forward at the roll's speed — the owner asked for
 * the possibility to be excluded outright rather than tidied up.
 *
 * Pure so the rule can be tested without Pixi; the caller decides how to say it.
 */
export function oneShotMoveRefusal(mounted: boolean): string | null {
  return mounted ? 'Not while you are riding — dismount first (E)' : null;
}

/** Running is the walk gait until the hero is actually mounted (Block 1: horses). */
export const applyGait = (c: FarmCtx) => {
  // A ridden horse keeps its gait — you don't sprint or slow a mount with Shift.
  if (c.riding?.mounted) return;
  c.player?.setGait(c.keys.has('ShiftLeft') || c.keys.has('ShiftRight') ? 'run' : 'walk');
};

export const onKeyDown = (c: FarmCtx) => (e: KeyboardEvent) => {
  // Ignore game keys while typing in an input (world chat, forms, etc.).
  const ae = document.activeElement as HTMLElement | null;
  if (ae && (ae.tagName === 'INPUT' || ae.tagName === 'TEXTAREA' || ae.isContentEditable)) return;
  // Alt = "the next hoe tap grasses the bed over". preventDefault stops the browser
  // from handing Alt to the OS menu bar, which would steal focus and swallow keyup.
  if (e.key === 'Alt') { e.preventDefault(); setClearModifier(c, true); return; }
  // A HELD key repeats at the OS rate (~30/s). That is one physical press, so it
  // must never read as thirty actions — holding E would toggle the saddle dozens
  // of times a second. Movement keys are exempt: they are level-triggered (the
  // key set decides the direction), and they already ignore a repeat via `keys`.
  if (e.repeat && !MOVE_KEYS.has(e.code) && !RUN_KEYS.has(e.code)) { e.preventDefault(); return; }
  const k = e.code;
  // Escape leaves placement mode. Build mode has no backdrop and no modal to
  // dismiss, so without this the only way to disarm was to find the Build button —
  // which is exactly the chrome that immersive mode hides.
  if (k === 'Escape' && c.buildMode) { e.preventDefault(); exitBuildMode(c); paintChrome(c); toast('Placement cancelled'); return; }
  if (RUN_KEYS.has(k)) { if (!c.keys.has(k)) { c.keys.add(k); applyGait(c); } return; }
  const oneShot = MOVE_ONESHOT[k];
  if (oneShot) {
    e.preventDefault();
    if (keyOnCooldown(k)) return;
    // Being mounted is a STATE, not an event: the answer is the same on the
    // ninetieth press as on the first, so `notifyOnce` says it once and goes quiet
    // until the answer changes (dismounting clears it — see the KeyE branch).
    const refusal = oneShotMoveRefusal(!!c.riding?.mounted);
    if (refusal) { notifyOnce('oneshot-move', refusal); return; }
    cancelCut(c);
    void c.player?.playMove(oneShot);
    return;
  }
  // P: take an acorn out / put it away. The gesture the tile bracket follows.
  if (k === 'KeyP') { e.preventDefault(); toggleAcornArmed(c); return; }
  // E: mount / dismount the tamed horse (only when standing next to it — there is
  // no recall, so reaching the horse is part of the cost of owning one).
  if (k === 'KeyE' && c.riding && c.player) {
    e.preventDefault();
    if (keyOnCooldown(k)) return;
    const px = c.player.x, py = c.player.y - FOOT_OFFSET;
    const r = c.riding.toggle(px, py);
    // Mounting and dismounting are EVENTS — they changed the world, so they always
    // speak. The two refusals are STATES: standing still with no horse in reach,
    // the game has nothing new to say on the ninetieth press that it did not say on
    // the first. `notifyOnce` says each once and goes quiet until the answer
    // differs, which is why the 300ms key cooldown was never the fix — it only
    // decided how FAST the same sentence repeated.
    if (r === 'mounted' || r === 'dismounted') notifyOnce('horse', null);
    // Getting off changes the answer to "may I roll?", so let it be said again.
    if (r === 'dismounted') notifyOnce('oneshot-move', null);
    if (r === 'mounted') { playSfx('horse_mount'); toast('Mounted 🐴'); }
    else if (r === 'dismounted') { c.player.setGait('walk'); toast('Dismounted'); }
    else if (r === 'too-far') notifyOnce('horse', 'Too far from your horse');
    else notifyOnce('horse', 'You have no horse — tame one');
    applyGait(c);
    return;
  }
  if (!MOVE_KEYS.has(k)) return;
  e.preventDefault();
  cancelCut(c);                       // walking away interrupts a hold-to-cut (and frees the hero to move)
  if (!c.keys.has(k)) { c.keys.add(k); pushKeys(c); }
};

export const onKeyUp = (c: FarmCtx) => (e: KeyboardEvent) => {
  if (e.key === 'Alt') { e.preventDefault(); setClearModifier(c, false); return; }
  if (RUN_KEYS.has(e.code)) { c.keys.delete(e.code); applyGait(c); return; }
  if (c.keys.delete(e.code)) pushKeys(c);
};

export const onTap = (c: FarmCtx) => (e: FederatedPointerEvent) => {
  const { cam, player } = c;
  if (!cam || !player || cam.dragged) return;
  const sc = cam.getScale();
  const wx = (e.global.x - cam.world.x) / sc, wy = (e.global.y - cam.world.y) / sc;
  dispatchTap(c, wx, wy);
};

// The tap dispatcher in world coordinates — shared by pointer taps and the mobile
// action button (which taps the tile the hero faces).
export function dispatchTap(c: FarmCtx, wx: number, wy: number) {
  const { player } = c;
  if (!player) return;
  // Build mode owns every click: place on eligible tiles, swallow the rest.
  if (c.buildMode) { toolTap(c, wx, wy); return; }
  // So does acorn mode, and for the same reason: while it is armed every tap is
  // "put an acorn there". Claimed BEFORE the tree/mob branches below so a tap on a
  // tile that happens to hold a tree explains the refusal instead of chopping it.
  if (isAcornArmed(c)) { toolTap(c, wx, wy); return; }
  // §2.5: one pending intent at a time. Any new tap cancels the previous
  // walk-to-operate / walk-to-plot target, the old path, and any in-progress cut
  // BEFORE the branches run — so a queued chop can't still fire after you click a
  // mob, animal, dungeon, or a different tile. Branches below set the new intent.
  c.pendingAction = null;
  c.pendingPlot = null;
  // A painted run is an intent like any other: a fresh tap somewhere else means the
  // player changed his mind, and marching on to the rest of the old stroke would be
  // the hero ignoring the click he just made.
  c.tillQueue.length = 0;
  c.pendingTalk = null;
  c.pendingMount = false;
  c.combatTargetId = null;   // a fresh tap disengages; attackMob re-locks if it hit a mob
  c.walkGoal = null;
  // Click the tamed horse → mount it (walk over first if needed).
  const horse = c.riding?.position();
  const horsePx = c.riding?.pixel();
  // Mounted or not: clicking the horse toggles. Riding then clicking it again is
  // the obvious way to get down, and it used to do nothing at all because this
  // branch was gated on `!mounted` — the only way off was the E key.
  if (horse && horsePx && c.riding && c.riding.type) {
    // Hit-test the horse's drawn BODY, not the single tile under its feet: it is
    // ~2.4 tiles tall, so clicking the saddle landed 2-3 tiles above its foot tile
    // and the old ±1-tile check missed every click on the visible animal.
    // Same window shape fauna/mobs use for their larger sprites.
    if (Math.abs(wx - horsePx.x) < 40 && wy - horsePx.y < 14 && wy - horsePx.y > -80) {
      const r = c.riding.toggle(player.x, player.y - FOOT_OFFSET);
      if (r === 'mounted') { playSfx('horse_mount'); player.setGait('mount'); toast('Mounted 🐴'); return; }
      if (r === 'dismounted') { player.setGait('walk'); notifyOnce('oneshot-move', null); toast('Dismounted'); return; }
      c.pendingMount = true;
      if (!walkTo(c, horse.tx, horse.ty)) player.setTarget((horse.tx + 0.5) * TILE, (horse.ty + 0.85) * TILE);
      return;
    }
  }
  player.setPath([]);        // stop walking toward the old target
  cancelCut(c);
  // A dungeon entrance under the pointer: descend (Block 6).
  const dg = dungeonUnderPointer(c, wx, wy);
  if (dg && c.onEnterDungeon) {
    const seed = ((dg.tx * 73856093) ^ (dg.ty * 19349663)) >>> 0;
    c.onEnterDungeon(seed);
    return;
  }
  // The birch-grove oracle under the pointer: walk over and open its errand dialog.
  const orc = oracleUnderPointer(c, wx, wy);
  if (orc && c.onTalkOracle) {
    if (nearObject(c, orc)) { c.onTalkOracle(orc); return; }
    const spot = adjacentTile(c, orc);
    if (!spot) { notifyOnce(`unreachable:${orc.id}`, unreachableSay(orc.kind), 'error'); return; }
    c.pendingTalk = orc;
    const [atx, aty] = spot;
    if (!walkTo(c, atx, aty)) player.setTarget((atx + 0.5) * TILE, (aty + 0.85) * TILE);
    return;
  }
  // An animal under the pointer wins over whatever it is standing on.
  // A hostile takes priority over an animal or a resource on the same tile.
  if (c.mobs && attackMob(c, wx, wy)) return;
  if (c.fauna && attackAnimal(c, wx, wy)) return;
  // Water in rod reach → cast. Sits BELOW the mob/animal checks (a fish is never
  // more urgent than something biting you) and ABOVE click-to-walk, because a tap
  // on water used to be a walk order into a lake the hero could not enter.
  {
    const wtx = Math.floor(wx / TILE), wty = Math.floor(wy / TILE);
    if (c.world && c.world.terrain[wty * c.world.cols + wtx] === 'water') {
      const why = castBlocker(c, wtx, wty, c.fishCfg?.hasRod ?? false);
      if (!why) { void castAt(c, wtx, wty); return; }
      // Only speak up about the reachable failures. "Too far" on a lake the player
      // is nowhere near is noise: that tap is just a misclick on scenery.
      if (why !== 'Too far — stand on the bank') notifyOnce('fishing.cast', why);
      return;
    }
  }
  // Tapped a tree/rock? Head over and operate on arrival. Else walk to the spot.
  const obj = objectUnderPointer(c, wx, wy);
  if (obj) { autoSelectFor(c, obj.d); requestOperate(c, obj.d, obj.s); return; }   // auto-equip the right tool, then operate
  // A selected tool claims clicks on farm cells (incl. swallowing invalid ones).
  if (toolTap(c, wx, wy)) return;
  // Ground cover on the tapped tile: pull it up by hand. Tufts and wildflowers are
  // viewport-culled scatter rather than event-bearing sprites (there are tens of
  // thousands of them), so they are found by tile here instead of under the pointer.
  if (clearFoliageAt(c, Math.floor(wx / TILE), Math.floor((wy + FOOT_OFFSET) / TILE))) return;
  // Ground click: path around barriers to the tile. + FOOT_OFFSET so the FEET
  // land on the clicked spot (the sprite's anchor sits above its feet).
  c.pendingAction = null;
  c.pendingPlot = null;
  const gx = wx, gy = wy + FOOT_OFFSET;
  const open = nearestOpenTile(c, Math.floor(gx / TILE), Math.floor(gy / TILE));
  if (!open || !walkTo(c, open[0], open[1])) player.setTarget(gx, gy);
}

// Mobile context-action: mount the horse if adjacent, else act on the tile the hero
// faces (chop / mine / gather / attack / till). The joystick handles movement; this
// is the "do something" button touch play otherwise lacks.
export function primaryAction(c: FarmCtx) {
  const p = c.player;
  if (!p) return;
  const feetY = p.y - FOOT_OFFSET;
  // Mount / dismount when next to (or on) the horse.
  if (c.riding?.type) {
    const r = c.riding.toggle(p.x, feetY);
    if (r === 'mounted') { toast('Mounted 🐴'); return; }
    if (r === 'dismounted') { p.setGait('walk'); notifyOnce('oneshot-move', null); toast('Dismounted'); return; }
    // 'too-far' / 'no-horse' → fall through to a normal action
  }
  const ptx = Math.floor(p.x / TILE), pty = Math.floor(feetY / TILE);
  const ftx = ptx + (p.dir === 'left' ? -1 : p.dir === 'right' ? 1 : 0);
  const fty = pty + (p.dir === 'up' ? -1 : p.dir === 'down' ? 1 : 0);
  const fwx = (ftx + 0.5) * TILE, fwy = (fty + 0.9) * TILE;
  // Planting, on touch, with no chrome to arm it.
  //
  // The keyboard toggle is not an option on a phone and this module does not own
  // the hotbar, so the action button — "do something on the tile I face" — plants
  // when that is the only thing there is to do: an acorn in the bag, bare grass
  // with its clearance ring free (plantBlocker decides, the same call the desktop
  // bracket uses), and nothing alive standing on it. On such a tile the button
  // currently does nothing at all, so this takes no gesture away from anything.
  if (!isAcornArmed(c) && acornCount(c) > 0 && canPlantHere(c, ftx, fty) && !creatureAt(c, fwx, fwy)) {
    void plantAcornAt(c, ftx, fty);
    return;
  }
  dispatchTap(c, fwx, fwy);
}

/** Is a mob or an animal standing under this world point? Mirrors the hit windows
 *  attackMob/attackAnimal use, as a QUESTION — they are actions and would swing. */
function creatureAt(c: FarmCtx, wx: number, wy: number): boolean {
  const near = (e: { x: number; y: number }) => Math.abs(wx - e.x) < 34 && wy - e.y < 12 && wy - e.y > -70;
  return !!c.mobs?.entities.some((m) => m.state !== 'dying' && m.state !== 'spawning' && near(m))
    || !!c.fauna?.entities.some((e) => e.state !== 'dead' && near(e));
}

// ─── Resource hover: tool cursor + white selector bracket ─────────────────────
// We hit-test the live prop sprites ourselves (their own pointer events are
// unreliable through the culled/render-grouped layers), so hovering ANY operable
// resource shows the RIGHT tool cursor (axe for trees, pickaxe for ore, a grab
// hand for gathers) and frames it with the UI bracket.
// Cursors are built from the player's tool→icon mapping (Tools dashboard), so the
// axe/pickaxe/hoe pointer always matches whatever icon they chose for that tool.
const toolCursor = (id: ToolId) => `url(${toolCursorUrl(id)}) 6 6, pointer`;
const HOE_CURSOR_CSS = () => toolCursor('hoe');
const WATER_CURSOR_CSS = () => `url(${waterCursorUrl()}) 6 6, pointer`;

/**
 * What a tap on this plot would actually do, as far as the POINTER cares.
 *
 * Mirrors the branch order in onPlot (plots.ts): a ready crop is harvested, an
 * unwatered bed is watered, anything else is hoe work. Watering is the one that
 * uses a different implement — the hero raises a watering can for it — so it is
 * the one the cursor has to distinguish.
 */
export function plotCursorTool(p: { state?: string; cropId?: string | null; ready?: boolean; watered?: boolean }): 'hoe' | 'water' {
  const planted = p.state === 'PLANTED' && !!p.cropId;
  if (planted && p.ready) return 'hoe';      // harvesting is done with the hoe
  return p.watered ? 'hoe' : 'water';
}

/** The cursor that matches how you harvest this prop. */
function cursorForDecor(d: Decor): string {
  if (d.kind === 'tree') return toolCursor('axe');                        // chop
  if (d.kind === 'ore_rock' || d.kind === 'rock' || d.kind === 'boulder') return toolCursor('pickaxe'); // mine / stone / big rock
  return 'pointer';                                                   // bush / mushrooms — grab by hand
}

// Pixi owns the canvas cursor: on every pointermove its EventSystem rewrites
// canvas.style.cursor from the hit target's `cursor`. The whole farm is one big
// hitArea on the stage, so the reliable lever is the STAGE's cursor — Pixi applies
// it itself (a raw CSS string is used verbatim when it's not a named style). We
// also poke the canvas + stageHost directly as belt-and-braces.
function setCursor(c: FarmCtx, css: string) {
  const val = css || 'default';
  if (c.app) c.app.stage.cursor = val;
  const canvas = c.app?.canvas as HTMLCanvasElement | undefined;
  if (canvas) canvas.style.cursor = val;
  c.stageHost.style.cursor = val;
}

export const onHover = (c: FarmCtx) => (e: FederatedPointerEvent) => {
  const { cam, app, selector } = c;
  if (!cam || !app || !selector) return;
  const sc = cam.getScale();
  const wx = (e.global.x - cam.world.x) / sc, wy = (e.global.y - cam.world.y) / sc;
  updateToolFrame(c, wx, wy);                      // tool target frame follows the pointer

  const obj = objectUnderPointer(c, wx, wy);
  if (obj) {
    const { s: t, d } = obj;
    setCursor(c, cursorForDecor(d)); c.treeCursorOn = true; lastHoverHitAt = Date.now();
    selector.x = t.x;
    selector.y = t.y - t.height * 0.16;                      // lower part, just above the base
    // §3: green when the hero is close enough to act, white when he'd have to
    // walk over first; the same bracket breathes gently.
    const inReach = nearObject(c, d);
    selector.tint = inReach ? FRAME_COLOR.ok : FRAME_COLOR.far;
    const size = Math.max(TILE * 0.9, Math.min(t.width * 0.55, TILE * 1.6));
    const breathe = 1 + Math.sin(performance.now() / 220) * 0.06;
    selector.scale.set((size / 16) * breathe);              // 16 = native bracket px (scale, not .width)
    selector.visible = true;
    return;
  }

  // Over the grove oracle → a talk pointer + selector bracket (green when in reach).
  const orc = oracleUnderPointer(c, wx, wy);
  if (orc) {
    const os = [...c.activeTall.values()].find((s) => (s as unknown as { decor?: Decor }).decor === orc);
    setCursor(c, 'pointer'); c.treeCursorOn = true; lastHoverHitAt = Date.now();
    if (os) {
      selector.x = os.x; selector.y = os.y - os.height * 0.16;
      selector.tint = nearObject(c, orc) ? FRAME_COLOR.ok : FRAME_COLOR.far;
      const breathe = 1 + Math.sin(performance.now() / 220) * 0.06;
      selector.scale.set((TILE * 1.1 / 16) * breathe);
      selector.visible = true;
    }
    return;
  }

  // Over a soil plot: the cursor names the IMPLEMENT the tap would raise — a
  // watering can for a dry bed, the hoe for everything else. It used to be the hoe
  // for all four of till/water/plant/harvest.
  const tx = Math.floor(wx / TILE), ty = Math.floor(wy / TILE);
  const plot = plotAt(c, tx, ty);
  if (plot) {
    setCursor(c, plotCursorTool(plot) === 'water' ? WATER_CURSOR_CSS() : HOE_CURSOR_CSS());
    c.treeCursorOn = true; lastHoverHitAt = Date.now();
    selector.visible = false;
    return;
  }

  // NOTHING under the pointer — but not necessarily nothing THERE.
  //
  // OWNER (2026-08-17): "there's a microswitch of the crosshair when I apply to the
  // tree — for a small second the arrow can appear, not the axe."
  //
  // objectUnderPointer walks c.activeTall, and that map is rebuilt continuously:
  // cullTall destroys and recreates sprites as they enter and leave vision, the
  // 1 Hz paintPlots repaints, and clearDecorUnderPlots toggles `visible`. For the
  // frame in which the tree under the cursor is missing or mid-rebuild the hit test
  // finds nothing, and this branch used to reset the cursor to the arrow at once —
  // the next pointermove found the tree again and put the axe back. A flicker with
  // no cause the player can see.
  //
  // So a miss has to persist before it is believed. HOVER_GRACE_MS is far longer
  // than a rebuild (one or two frames) and far shorter than a deliberate move off
  // a tree, so the arrow still returns immediately in every case that is real.
  if (c.treeCursorOn && Date.now() - lastHoverHitAt > HOVER_GRACE_MS) {
    setCursor(c, '');
    c.treeCursorOn = false;
    selector.visible = false;
  } else if (!c.treeCursorOn) {
    selector.visible = false;
  }
};

/**
 * How long a hover miss must persist before the cursor drops back to the arrow.
 *
 * A sprite rebuild lasts a frame or two (~16-33ms); a player moving off a tree
 * takes far longer than 200ms to do anything that depends on the cursor. The
 * bracket is held for the same window, for the same reason — it blinked too.
 */
const HOVER_GRACE_MS = 200;
/** When the hover last actually found something. Module scope: there is one stage. */
let lastHoverHitAt = 0;

// ─── Walk-to-object, then operate ─────────────────────────────────────────────
// Clicking a tree/rock heads the hero to an adjacent tile and starts the action
// on arrival — you no longer need to already be standing on it.
export function objectUnderPointer(c: FarmCtx, wx: number, wy: number): { s: Sprite; d: Decor } | null {
  let best: Sprite | null = null, bestY = -Infinity;
  for (const s of c.activeTall.values()) {
    const d = (s as unknown as { decor?: Decor }).decor;
    if (!d || !s.visible) continue;
    const usable = (d.kind === 'tree' && !isCleared(c, d)) || (d.kind === 'ore_rock' && !isMined(c, d))
      || (GATHER_KINDS.has(d.kind) && !isGathered(c, d));
    if (!usable) continue;
    const w = s.width * 1.1, h = s.height;
    if (wx >= s.x - w / 2 && wx <= s.x + w / 2 && wy >= s.y - h - 4 && wy <= s.y - h * 0.05 && s.y > bestY) { best = s; bestY = s.y; }
  }
  return best ? { s: best, d: (best as unknown as { decor: Decor }).decor } : null;
}

// §4b: is there a standing tree (or un-grubbed stump) occupying this tile? Such a
// tile can't be tilled/built until it's chopped and the stump grubbed out.
export function treeOnTile(c: FarmCtx, tx: number, ty: number): boolean {
  for (const s of c.activeTall.values()) {
    const d = (s as unknown as { decor?: Decor }).decor;
    if (d && d.kind === 'tree' && d.tx === tx && d.ty === ty && !isCleared(c, d)) return true;
  }
  return false;
}

/** A dungeon entrance sprite under the pointer (Block 6). */
export function dungeonUnderPointer(c: FarmCtx, wx: number, wy: number): Decor | null {
  for (const s of c.activeTall.values()) {
    const d = (s as unknown as { decor?: Decor }).decor;
    if (!d || d.kind !== 'dungeon' || !s.visible) continue;
    const w = s.width * 1.1, h = s.height;
    if (wx >= s.x - w / 2 && wx <= s.x + w / 2 && wy >= s.y - h - 4 && wy <= s.y - h * 0.05) return d;
  }
  return null;
}

/** The birch-grove oracle NPC sprite under the pointer. */
export function oracleUnderPointer(c: FarmCtx, wx: number, wy: number): Decor | null {
  for (const s of c.activeTall.values()) {
    const d = (s as unknown as { decor?: Decor }).decor;
    if (!d || d.kind !== 'oracle' || !s.visible) continue;
    const w = s.width * 1.2, h = s.height;
    if (wx >= s.x - w / 2 && wx <= s.x + w / 2 && wy >= s.y - h - 4 && wy <= s.y - h * 0.05) return d;
  }
  return null;
}

/**
 * The tile to stand on to work this node — or null when there is not one.
 *
 * Null matters. This used to fall back to `[d.tx, d.ty + 1]` when every
 * neighbour was solid, and that tile is blocked too, so `walkTo` failed, the
 * straight-line fallback walked the hero into rock, and the node never came into
 * reach. The player tapped a boulder and the game did nothing at all, with no
 * message — owner: "big stones are not reachable, can't mine them." World gen no
 * longer walls stone in (see the sweep in map/world.ts), but a player can still
 * fence or build a node in, so the caller has to be able to SAY so.
 *
 * Only the eight neighbours are candidates: reach IS the eight neighbours (see
 * `nearObject`), so standing further away and swinging is not a thing the rest of
 * the code supports.
 */
function adjacentTile(c: FarmCtx, d: Decor): [number, number] | null {
  const [ptx, pty] = feetTile(c.player!);
  const cand: [number, number][] = [[d.tx + 1, d.ty], [d.tx - 1, d.ty], [d.tx, d.ty + 1], [d.tx, d.ty - 1],
    [d.tx + 1, d.ty + 1], [d.tx - 1, d.ty + 1], [d.tx + 1, d.ty - 1], [d.tx - 1, d.ty - 1]];
  let best: [number, number] | null = null, bd = Infinity;
  for (const [tx, ty] of cand) {
    if (tileBlocked(c, tx, ty)) continue;
    const dd = Math.hypot(tx - ptx, ty - pty);
    if (dd < bd) { bd = dd; best = [tx, ty]; }
  }
  return best;
}

/** What to say when a node is ringed in. Named per kind — "it" is not an answer. */
export const unreachableSay = (kind: string): string =>
  (kind === 'boulder' || kind === 'rock' ? 'No way in — clear the rocks around it first'
    : kind === 'ore_rock' ? 'No way in — clear what is around the vein first'
    : kind === 'tree' ? 'No way in — clear around the tree first'
    : 'No way in — clear a path to it first');

/**
 * The hero's tile, measured at his FEET.
 *
 * `player.x/y` is the SPRITE ANCHOR, and the feet sit FOOT_OFFSET (0.832 tile)
 * above it. Every other spatial system in the client — collision, pathfinding,
 * `inToolReach`, the audio listener — already works in feet space; reach did not,
 * which put its idea of the hero almost a whole tile south of where he stands.
 */
const feetTile = (p: { x: number; y: number }): [number, number] =>
  [Math.floor(p.x / TILE), Math.floor((p.y - FOOT_OFFSET) / TILE)];

/**
 * Can the hero work this node from where he stands?
 *
 * Strict 8-neighbour Chebyshev on the hero's FEET tile — the same rule as
 * `inToolReach`, and exactly the contract `adjacentTile` promises. It used to be
 * a 1.7-tile euclidean radius measured from the sprite anchor, and the arithmetic
 * never closed: standing on the S/SE/SW neighbour put the anchor 1.83 tiles from
 * the node's centre, so the predicate said "too far" about a tile the pathfinder
 * had just walked to on purpose. The tap was then dropped in silence — owner:
 * "big stones are not reachable." Approaching from the north worked, which is why
 * it looked random.
 *
 * A tile rule cannot let the hero mine through a wall: the only tiles he can
 * stand on are open ones (`tileBlocked`), and one tile of separation is all
 * Chebyshev <= 1 ever allows.
 */
export const nearObject = (c: FarmCtx, d: Decor) => nearTile(c, d.tx, d.ty);


/**
 * Pull up the grass tuft or wildflower standing on this tile.
 *
 * Ground cover lives in the viewport-culled scatter layer, not the event-bearing
 * entity layer — there are tens of thousands of tufts, and giving each one a hit
 * area was what made the old build spend its frame budget on hover tests. So the
 * lookup goes through the scatter bucket index by tile instead, and the sprite is
 * hidden through `activeSmall`.
 *
 * Returns true when it claimed the tap (so the caller doesn't also walk there).
 * Out of reach, the hero walks over and clears it on arrival.
 */
export function clearFoliageAt(c: FarmCtx, tx: number, ty: number): boolean {
  const list = c.smallBuckets.get(bkey(tx, ty));
  if (!list) return false;
  const d = list.find((x) => x.tx === tx && x.ty === ty && FOLIAGE_KINDS.has(x.kind) && !isGathered(c, x));
  if (!d) return false;
  const entry = c.activeSmall.get(d.id);
  if (!entry || !entry.s.visible) return false;
  if (!nearObject(c, d)) {
    // Too far: walk over, then clear on arrival (same shape as chopping a tree).
    const spot = adjacentTile(c, d);
    if (!spot) { notifyOnce(`unreachable:${d.id}`, unreachableSay(d.kind), 'error'); return true; }
    c.pendingAction = { d, s: entry.s };
    const [atx, aty] = spot;
    if (!walkTo(c, atx, aty)) c.player?.setTarget((atx + 0.5) * TILE, (aty + 0.85) * TILE);
    return true;
  }
  void gatherFoliage(c, d, entry.s);
  return true;
}

export function requestOperate(c: FarmCtx, d: Decor, s: Sprite) {
  if (!c.player) return;
  c.pendingPlot = null;
  if (nearObject(c, d)) { c.pendingAction = null; startCut(c, d, s); return; }
  const spot = adjacentTile(c, d);
  if (!spot) {
    // Ringed in: queuing a walk here is how the tap became a no-op. Say it
    // instead, once per node, so the player knows there IS something to do.
    notifyOnce(`unreachable:${d.id}`, unreachableSay(d.kind), 'error');
    return;
  }
  notifyOnce(`unreachable:${d.id}`, null);
  c.pendingAction = { d, s };
  const [atx, aty] = spot;
  if (!walkTo(c, atx, aty)) c.player.setTarget((atx + 0.5) * TILE, (aty + 0.85) * TILE);   // fallback: straight line
}

export const nearTile = (c: FarmCtx, tx: number, ty: number) => {
  if (!c.player) return false;
  const [ptx, pty] = feetTile(c.player);
  return Math.max(Math.abs(tx - ptx), Math.abs(ty - pty)) <= 1;
};

export function requestPlot(c: FarmCtx, tx: number, ty: number, tool?: 'hoe', intent: HoeIntent = 'auto') {
  if (!c.player) return;
  c.pendingAction = null;
  if (nearTile(c, tx, ty)) { c.pendingPlot = null; actOnPlot(c, tx, ty, tool, intent); return; }
  // The intent travels WITH the walk order: Alt is long released by the time the
  // hero arrives, so re-reading the modifier on arrival would turn a "grass it
  // over" order into a plain tap on the bed (watering it instead).
  c.pendingPlot = { tx, ty, tool, intent };
  // Plot cells are walkable — path onto the cell itself (nearest open as fallback).
  const goal = tileBlocked(c, tx, ty) ? nearestOpenTile(c, tx, ty) : ([tx, ty] as [number, number]);
  if (!goal || !walkTo(c, goal[0], goal[1])) c.player.setTarget((tx + 0.5) * TILE, (ty + 0.85) * TILE);  // fallback: straight line
}

// ─── Tool targeting: hovered-tile frame + direct tool clicks ──────────────────
// With a tool selected the hovered tile is framed: GREEN = the tool works here
// and the hero is within 1 tile (click acts instantly); WHITE = the tool works
// here but it's out of reach (click walks over, then acts); RED = the tool
// can't operate this cell (click does nothing — no accidental walking).
const FRAME_COLOR = { ok: 0x6ee76e, far: 0xffffff, no: 0xf05555 } as const;

// Block 8: a tile is tillable/buildable if it's cleared ground — grass terrain,
// in bounds, not blocked by decor/water/landmark, and not already a plot or
// building. The client owns the map, so this is where eligibility is decided; the
// server only enforces the soft cap.
export function isClearedTile(c: FarmCtx, tx: number, ty: number): boolean {
  const { world } = c;
  if (!world) return false;
  if (tx < 0 || ty < 0 || tx >= world.cols || ty >= world.rows) return false;
  if (world.terrain[ty * world.cols + tx] !== 'grass') return false;
  if (tileBlocked(c, tx, ty)) return false;             // tree/rock/water/landmark
  return true;
}

/**
 * Props that COME BACK after you clear them, so their tile stays reserved.
 *
 * Bushes, mushrooms and ground cover respawn in a few hours. Tilling their tile
 * while they are gone would put a bed exactly where the prop is about to grow
 * again — the bed would end up drawn under it. Everything else tall (trees,
 * rocks, boulders, veins) is cleared PERMANENTLY, which is the whole point of the
 * harvest-persistence work: land you clear is land you have won.
 */
const REGROWING_KINDS = new Set(['mushrooms', 'bush', 'grass_tuft', 'flowers']);

/**
 * Is this prop still standing on its tile?
 *
 * OWNER BUG (2026-08-14): "it's showing me message clear what is standing here
 * first, but i can't see, nothing is here." Correct — nothing was. This test used
 * to ignore cleared state entirely, on the reasoning that props regrow. That was
 * true when it was written and is not true now: trees you fell and stumps you grub
 * are gone for a century (CLEARED_FOREVER_MS), as are mined veins and broken
 * boulders. So every tile a player had ever CLEARED stayed permanently un-tillable
 * while looking completely empty — the exact tiles cleared in order to farm them.
 */
function stillStanding(c: FarmCtx, d: { kind: string; id: string; variant?: number }): boolean {
  if (REGROWING_KINDS.has(d.kind)) return true;          // gone for now, back by evening
  const dd = d as Parameters<typeof isCleared>[1];
  if (dd.kind === 'tree') return !isCleared(c, dd);      // stump still counts; bare ground does not
  if (isMined(c, dd) || isGathered(c, dd)) return false; // veins and rocks: cleared for good
  return true;                                            // hillocks, wells, fences: permanent scenery
}

/** A tall prop whose ORIGIN tile is (tx,ty) and which is still actually there. */
function tallDecorOnTile(c: FarmCtx, tx: number, ty: number): boolean {
  const list = c.tallBuckets.get(bkey(tx, ty));
  return !!list && list.some((d) => d.tx === tx && d.ty === ty && TALL_KINDS.has(d.kind) && stillStanding(c, d));
}
/**
 * Is there a tall object standing ON or NEXT TO this tile?
 *
 * OWNER RULE (2026-08-14): "if there is tree, stone, or any other object, house
 * — we shouldn't be able to make around it +1 block, to not create overlays of
 * the sprites".
 *
 * Everything tall in this game is anchored at its base and drawn upward, taller
 * and wider than the single tile it occupies: a house is ~2 tiles of art, a tree
 * about the same. A bed tilled against one is drawn UNDER that art, so the soil
 * disappears behind a wall or a trunk and the player loses a bed he paid for. A
 * one-tile ring is the cheapest rule that guarantees the bed is never covered.
 *
 * The ring is the full 8 neighbours, not just the row a canopy reaches: a house
 * is wide as well as tall, so the tile to its left is as covered as the one above.
 */
function tallNear(c: FarmCtx, tx: number, ty: number): boolean {
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const nx = tx + dx, ny = ty + dy;
    const list = c.tallBuckets.get(bkey(nx, ny));
    if (list?.some((d) => d.tx === nx && d.ty === ny && TALL_KINDS.has(d.kind) && stillStanding(c, d))) return true;
  }
  return false;
}

/**
 * Is this tile inside the space some building already reserves?
 *
 * The clearance rule (BUILDING_CLEAR) and the box it describes now live in
 * placement.ts — see insideReservedSpace. This used to walk its own ±2/-1 box
 * around the candidate tile, measured from each building's ANCHOR only, which was
 * wrong in the one direction that costs the player something: a farmhouse is three
 * tiles wide, so the beds beside its far column sat outside the box, were legal to
 * till, and were then drawn underneath the wall. It is also the set the new
 * "building destroys the beds under it" rule deletes, and the two MUST be the same
 * set — otherwise the hoe lets you pay energy for a bed the next placement wipes.
 */
function buildingNear(c: FarmCtx, tx: number, ty: number): boolean {
  return insideReservedSpace(c.farm?.buildings ?? [], tx, ty);
}

/**
 * WHY the hoe refuses this tile, in words, or null if it does not.
 *
 * Owner: "I don't understand, why i can't on particular block, to use the hoe,
 * there's no any problems with other element". He was right that it looked
 * arbitrary — only one of the six refusals said anything ("Clear the tree
 * first"); the rest swallowed the tap and let him walk, which is indistinguishable
 * from a broken tool. Mirrors buildBlocker, which already works this way.
 *
 * Order matters: report the thing standing ON the tile before the thing beside it.
 */
export function tillBlocker(c: FarmCtx, tx: number, ty: number, intent: HoeIntent = 'auto'): string | null {
  const { world } = c;
  // Clearing asks the opposite question of tilling — it wants a bed, not bare
  // ground — so it gets its own reasons rather than being squeezed through the
  // grass rules below (which would answer "beds only go on grass" while standing
  // on a bed, i.e. nonsense).
  if (intent === 'clear') {
    const p = plotAt(c, tx, ty);
    if (!p) return 'Nothing to grass over — this is not a soil bed';
    if (p.state === 'PLANTED' && p.cropId) return 'Harvest the crop before you grass this bed over 🌾';
    return null;
  }
  if (!world) return 'Still loading…';
  if (tx < 0 || ty < 0 || tx >= world.cols || ty >= world.rows) return 'Outside the farm';
  if (buildingAt(c, tx, ty)) return 'Something is built here';
  if (tallDecorOnTile(c, tx, ty)) return 'Clear what is standing here first 🌳';
  if (world.terrain[ty * world.cols + tx] !== 'grass') return 'Beds only go on grass';
  if (tileBlocked(c, tx, ty)) return 'Blocked — clear this tile first';
  // The +1 ring. Both messages name the thing AND say how far to move, because
  // "you can't" without "then where" is the complaint this whole rule caused.
  if (buildingNear(c, tx, ty)) return 'Too close to a building — leave one tile clear around it';
  if (tallNear(c, tx, ty)) return 'Too close to a tree or rock 🌳 — leave one tile clear, or clear it';
  if (c.farm && c.farm.plots.length >= c.farm.maxPlots) {
    return `All ${c.farm.maxPlots} beds are in use — harvest or clear one first`;
  }
  return null;
}
/**
 * ONE rule, asked two ways.
 *
 * This used to list its own conditions, which drifted from the ones that actually
 * refused the tap — so the bracket could show green on a tile the hoe then
 * rejected in silence. It now defers to tillBlocker: same answer for the frame,
 * the click and the message.
 */
export const canTillHere = (c: FarmCtx, tx: number, ty: number) =>
  !plotAt(c, tx, ty) && tillBlocker(c, tx, ty) === null;

// ─── Acorns: plant a tree on bare grass ──────────────────────────────────────
/**
 * The pseudo-tool that arms planting.
 *
 * It rides on `selectedTool` rather than a state of its own so it behaves exactly
 * like the hoe everywhere that already reads that field: the tile bracket paints,
 * a tap is claimed by toolTap instead of walking, and selecting any real tool
 * disarms it. It is deliberately NOT in the hoe's flow — an acorn is not a crop and
 * must never ask for a tilled bed (the server refuses one: "An acorn goes in the
 * grass, not in a bed").
 */
export const ACORN_TOOL = 'acorn';
export const ACORN_ITEM_ID = 'acorn';
export const isAcornArmed = (c: FarmCtx) => c.selectedTool === ACORN_TOOL;
/** Acorns in the bag, from the cache the hotbar already refreshes after every action. */
export const acornCount = (c: FarmCtx): number =>
  c.bagCache?.items.find((i) => i.itemId === ACORN_ITEM_ID)?.quantity ?? 0;

/**
 * WHY an acorn cannot go here, in words, or null if it can.
 *
 * OWNER (2026-08-15): "PLANT ANYWHERE ON GRASS" — so this deliberately does NOT
 * consult the plot allowance or the bed flow at all; a sapling is not a crop and
 * costs no bed. Everything else it shares with tillBlocker, on purpose:
 *
 *   THINK ABOUT THE ADULT TREE, NOT THE SEEDLING. The clearance ring exists
 *   because tall art is anchored at its base and drawn upward and outward, so
 *   anything placed against a house or a tree is drawn underneath it. A seedling
 *   is two pixels tall and would fit anywhere — but in six hours it is a full oak,
 *   permanently, one tile from someone's roof, and the only way out of that is to
 *   chop down the tree you just grew. So the sapling is judged by the footprint of
 *   what it becomes, which is the same footprint tillBlocker already enforces.
 *
 * Same shape as tillBlocker (message or null) so the bracket, the tap and the
 * toast can never disagree — see canPlantHere.
 */
export function plantBlocker(c: FarmCtx, tx: number, ty: number): string | null {
  const { world } = c;
  if (!world) return 'Still loading…';
  if (tx < 0 || ty < 0 || tx >= world.cols || ty >= world.rows) return 'Outside the farm';
  if (acornCount(c) <= 0) return 'No acorns — fell trees and one may drop 🌰';
  if (plotAt(c, tx, ty)) return 'An acorn goes in the grass, not in a bed 🌱';
  if (buildingAt(c, tx, ty)) return 'Something is built here';
  if (tallDecorOnTile(c, tx, ty)) return 'Clear what is standing here first 🌳';
  if (world.terrain[ty * world.cols + tx] !== 'grass') return 'Acorns only grow in grass';
  if (tileBlocked(c, tx, ty)) return 'Blocked — clear this tile first';
  // The +1 ring, judged on the ADULT tree (see the note above). Both messages say
  // how far to move, because "no" without "then where" is the complaint the hoe's
  // silent refusals caused.
  if (buildingNear(c, tx, ty)) return 'Too close to a building — a grown tree would swallow it. Leave one tile clear';
  if (tallNear(c, tx, ty)) return 'Too close to a tree or rock 🌳 — leave one tile clear so it has room to grow';
  return null;
}

/** ONE rule, asked two ways — the green bracket and the tap read the same call. */
export const canPlantHere = (c: FarmCtx, tx: number, ty: number) => plantBlocker(c, tx, ty) === null;

/**
 * Arm / disarm acorn planting.
 *
 * Exported so any chrome can drive it (the hotbar is not this module's to edit);
 * bound to P here so it is reachable today, and it is a TOGGLE rather than a hold
 * because planting a row of oaks is several taps and a held key is not a gesture
 * touch has. Selecting a real tool clears it, since both live in `selectedTool`.
 */
export function toggleAcornArmed(c: FarmCtx): boolean {
  cancelPlacementForHand(c, true);   // build ghost wins over the acorn bracket otherwise; the toast below says what is in hand
  c.selectedTool = isAcornArmed(c) ? null : ACORN_TOOL;
  if (c.tileFrame && !isAcornArmed(c)) c.tileFrame.visible = false;
  syncHeld(c);
  paintHotbar(c);
  toast(isAcornArmed(c)
    ? `Acorn ready 🌰 — tap bare grass to plant (${acornCount(c)} left)`
    : 'Acorn put away');
  return isAcornArmed(c);
}

/**
 * A plant the hero has to walk to first.
 *
 * Deliberately NOT routed through `pendingPlot`: that field carries a hoe intent
 * into actOnPlot, which is the tilled-bed flow this must never enter. Held here
 * instead and drained by pumpPendingPlant, which the farm frame loop calls on its
 * OWN line, so arrival plants exactly one acorn on the tile that was tapped.
 *
 * It used to be drained from inside pumpTillQueue, on the reasoning that the farm
 * tick already calls that. It does not call it unconditionally: the tick's line is
 * `else if (c.tillQueue.length && !c.pendingAction) pumpTillQueue(c)`, and a plant
 * tap never puts anything in tillQueue. So the queued plant was drained only if the
 * player happened to also have a painted bed run going — otherwise the hero walked
 * to the tile and stood there, and the tap looked ignored.
 */
let pendingPlant: { c: FarmCtx; tx: number; ty: number } | null = null;

export function requestPlantAcorn(c: FarmCtx, tx: number, ty: number) {
  if (!c.player) return;
  pendingPlant = { c, tx, ty };
  const goal = tileBlocked(c, tx, ty) ? nearestOpenTile(c, tx, ty) : ([tx, ty] as [number, number]);
  if (!goal || !walkTo(c, goal[0], goal[1])) c.player.setTarget((tx + 0.5) * TILE, (ty + 0.85) * TILE);
}

/** Test seam + teardown: a queued plant must not survive the view that queued it. */
export const __clearPendingPlant = () => { pendingPlant = null; };

/**
 * Fire a queued plant once the hero is standing next to the tile.
 *
 * Re-asks plantBlocker on ARRIVAL rather than trusting the answer from the tap: the
 * walk takes seconds, and in that time the last acorn can be spent elsewhere or a
 * building can go up beside the tile. Refusing then is the honest answer, with the
 * reason spoken.
 */
export function pumpPendingPlant(c: FarmCtx) {
  if (!pendingPlant || pendingPlant.c !== c) return;
  const { tx, ty } = pendingPlant;
  if (!inToolReach(c, tx, ty)) return;
  pendingPlant = null;
  const why = plantBlocker(c, tx, ty);
  if (why) { toast(why); return; }
  void plantAcornAt(c, tx, ty);
}

/**
 * Room left in the budget the SELECTED kind is charged against, and every other
 * placement rule, now live in placement.ts — one predicate shared by the ghost, the
 * tile frame and the click, so the preview can never disagree with the commit.
 */
export { canBuildHere, buildBlocker, isBridgeKind, hasRoomFor } from './placement';

/**
 * How the player ASKS for a bed to be grassed over.
 *
 * OWNER (2026-08-15): "with hoe, you can grass up again the plot". Destroying a
 * bed cannot ride on a plain tap: the hoe already tills, waters, seeds and
 * harvests on tap, so a mis-tap one tile off would delete the bed you meant to
 * water. It is therefore an explicit second gesture — hold Alt while tapping on
 * desktop, or the "Grass it over" button in the seed panel on touch — and it is
 * confirmed before it fires. Holding the modifier also re-tints the tile bracket,
 * because hoeAction and the frame read this same flag.
 */
export type HoeIntent = 'auto' | 'clear';

// Tracked on the modifier itself rather than read off the click event: the tile
// bracket is painted from pointermove/ticks, which have no key state, and a green
// frame that disagrees with what the tap does is the exact bug this file keeps
// fighting.
// Alt, NOT Shift: Shift is the run gait (RUN_KEYS above), so a shift-tap would
// sprint the hero at the bed it was about to delete.
//
// The listeners live in onKeyDown/onKeyUp/onWindowBlur below, which farm.ts binds
// and UNBINDS with the view. They used to be registered here at module scope with
// no removal (leaking a listener per module load, and still firing after the farm
// was torn down) and with no preventDefault — on platforms where Alt focuses the
// menu bar the window loses focus and the keyup never arrives, leaving the flag
// armed so the next plain hoe tap deleted a bed. preventDefault + the blur reset
// close both halves of that.
let clearModifierHeld = false;
export const hoeIntent = (): HoeIntent => (clearModifierHeld ? 'clear' : 'auto');
/** Test seam — the listeners need a real key event, which jsdom won't give Pixi. */
export const setHoeClearModifier = (held: boolean) => { clearModifierHeld = held; };

/**
 * Arm/disarm the clear modifier AND repaint the bracket.
 *
 * The repaint is the point: updateToolFrame only ran from pointermove, so pressing
 * or releasing Alt with a still cursor left the bracket tinted for the previous
 * intent until the mouse happened to move — the frame said "water" while the tap
 * would have grassed the bed over. Replaying the last pointer position through the
 * same function keeps frame and action in agreement without a tick loop.
 */
function setClearModifier(c: FarmCtx, held: boolean) {
  if (clearModifierHeld === held) return;
  clearModifierHeld = held;
  if (lastPointerCtx === c && lastPointer) updateToolFrame(c, lastPointer.wx, lastPointer.wy);
}

let lastPointerCtx: FarmCtx | null = null;
let lastPointer: { wx: number; wy: number } | null = null;

/** Alt-tab / menu-bar focus loss must not leave the destructive modifier armed. */
export const onWindowBlur = (c: FarmCtx) => () => setClearModifier(c, false);

// What the hoe would do at this tile (null = nothing → red frame).
export function hoeAction(
  c: FarmCtx, tx: number, ty: number, intent: HoeIntent = hoeIntent(),
): 'till' | 'harvest' | 'clear' | null {
  const p = plotAt(c, tx, ty);
  // Same rule object as the message: if tillBlocker has no objection to clearing
  // this tile, the bracket goes green and the tap does exactly that.
  if (intent === 'clear') return tillBlocker(c, tx, ty, 'clear') === null ? 'clear' : null;
  if (!p) return canTillHere(c, tx, ty) ? 'till' : null;   // bare cleared ground → hoe a new bed
  const planted = p.state === 'PLANTED' && !!p.cropId;
  if (planted && (p.ready || liveRemaining(c, p.remainingMs) <= 0)) return 'harvest';
  return null;
}

/**
 * Does the hoe DO anything on this tile? — which is a wider question than
 * `hoeAction`, and the frame has to ask this one.
 *
 * `hoeAction` answers only the three things the hoe branch of `actOnPlot`
 * handles directly (till, harvest, clear). It returns null for a tilled-but-dry
 * bed and for a watered-but-empty one — and yet a tap on either of those DOES
 * something: plots.ts falls through to `onPlot`, which waters the dry bed and
 * opens the seed picker on the wet one ("if it's an existing bed that still
 * needs watering or seeding, do THAT instead of silently doing nothing").
 *
 * So the bracket painted RED over the two tiles in the middle of the farming
 * loop — water it, then seed it — while the click worked perfectly. Owner: "when
 * I created a plot and I'm moving around, the frame should be green when I'm
 * watering it and next round when I'm seeding it."
 *
 * This file's history is full of the opposite mistake (a green frame over a tile
 * the tap then refuses), and the fix for that was to make the frame ask the same
 * question the tap asks. This is the same fix for the other direction: it mirrors
 * `actOnPlot`'s fall-through exactly, so the two cannot disagree.
 *
 * `hoeAction` is deliberately left alone. It drives the tap's till/harvest
 * branches and its three-value return is load-bearing there; widening it would
 * change what the hoe DOES, when all that is wrong is what the frame SAYS.
 */
export function hoeUsable(c: FarmCtx, tx: number, ty: number, intent: HoeIntent = hoeIntent()): boolean {
  if (hoeAction(c, tx, ty, intent) !== null) return true;
  if (intent === 'clear') return false;          // Alt is a clear-or-nothing intent
  const p = plotAt(c, tx, ty);
  if (!p) return false;                          // bare ground the hoe cannot till
  const planted = p.state === 'PLANTED' && !!p.cropId;
  // An unwatered bed can always be watered — planted (the regrow cycle) or not.
  if (!p.watered) return true;
  // A watered, empty bed opens the seed picker.
  return !planted;
}

// "1 block around" the hero: the hero's own tile + its 8 neighbours.
export const inToolReach = (c: FarmCtx, tx: number, ty: number): boolean => {
  if (!c.player) return false;
  const ptx = Math.floor(c.player.x / TILE), pty = Math.floor((c.player.y - FOOT_OFFSET) / TILE);
  return Math.max(Math.abs(tx - ptx), Math.abs(ty - pty)) <= 1;
};

export function updateToolFrame(c: FarmCtx, wx: number, wy: number) {
  // Remembered so an Alt press/release can repaint the bracket without waiting for
  // the pointer to move (see setClearModifier).
  lastPointerCtx = c; lastPointer = { wx, wy };
  const { tileFrame, world } = c;
  if (!tileFrame || !world) return;
  const inBuild = !!c.buildMode;
  if (c.selectedTool !== 'hoe' && !isAcornArmed(c) && !inBuild) { tileFrame.visible = false; hideBuildGhost(c); return; }
  const tx = Math.floor(wx / TILE), ty = Math.floor(wy / TILE);
  if (tx < 0 || ty < 0 || tx >= world.cols || ty >= world.rows) { tileFrame.visible = false; hideBuildGhost(c); return; }
  // Build mode: green = a cleared, free tile you can place on; red = anything else.
  // Hoe mode: green = works + in reach, white = works but out of reach, red = no.
  let color: number;
  if (inBuild) {
    // One evaluation feeds both the bracket and the ghost, so a red outline and a
    // red building can never disagree — and the reason is shown, not guessed at.
    const blocker = buildBlocker(c, tx, ty);
    color = blocker ? FRAME_COLOR.no : FRAME_COLOR.ok;
    updateBuildGhost(c, c.buildMode!, tx, ty, blocker);
  } else {
    hideBuildGhost(c);
    // Acorn mode: the bracket asks plantBlocker — the same call the tap makes — so a
    // green frame over a tile that then refuses is impossible.
    const usable = isAcornArmed(c) ? canPlantHere(c, tx, ty) : hoeUsable(c, tx, ty);
    color = !usable ? FRAME_COLOR.no : inToolReach(c, tx, ty) ? FRAME_COLOR.ok : FRAME_COLOR.far;
  }
  // §3: the UI-pack corner bracket, tinted per state, breathing (subtle scale
  // pulse) and lerped toward the hovered tile so it glides rather than snapping.
  const cx = (tx + 0.5) * TILE, cy = (ty + 0.5) * TILE;
  if (!tileFrame.visible) { tileFrame.x = cx; tileFrame.y = cy; }   // no lerp on first show
  else { tileFrame.x += (cx - tileFrame.x) * 0.35; tileFrame.y += (cy - tileFrame.y) * 0.35; }
  tileFrame.tint = color;
  // OWNER (2026-08-14): "shouldn't be so big and take so much space". At tile
  // scale the bracket's pips sat on the cell's own edges, so it read as a border
  // painted on the ground rather than a cursor hovering over it — and on a phone
  // it swallowed the tile art underneath. Pulled in to 72% of the cell, with the
  // pulse halved so it stays a marker instead of animating for attention.
  const breathe = 1 + Math.sin(performance.now() / 220) * 0.03;
  tileFrame.scale.set((TILE / 16) * 0.72 * breathe);
  tileFrame.visible = true;
}

// The tool an object wants: trees the axe, ore rocks the pickaxe. Small props
// (stones, mushrooms) are bare-hands gathers — no tool, so `null` leaves the
// current selection alone rather than deselecting.
export const idealTool = (d: Decor): 'axe' | 'pickaxe' | null => {
  if (d.kind === 'tree') return 'axe';
  // `rock` — the small ground stone — belongs here with the other two. It is MINED
  // (startCut gives it rock=true and MINE_SECONDS), but it was missing from this
  // table, so tapping one never auto-equipped the pick. Walk up from felling a
  // tree with the axe still in hand, tap a stone, and the hero swung the AXE at it
  // in the tree-chopping animation — the swing said "chopping wood" while the
  // action was mining stone.
  if (d.kind === 'ore_rock' || d.kind === 'boulder' || d.kind === 'rock') return 'pickaxe';
  return null;
};

// Auto-equip the right tool for a tapped object, so the hero visibly wields it
// and the hotbar reflects the switch. Removes the old "wrong tool → dead click"
// friction: any tap on a tree/rock just works.
export function autoSelectFor(c: FarmCtx, d: Decor) {
  const want = idealTool(d);
  if (!want) return;
  // An axe going into the hero's hands ends any armed placement, exactly like the
  // hotbar and the acorn key. The scene wires this to pointerdown on every tree,
  // prop and ore sprite WITHOUT a buildMode guard, so without this the red ghost
  // survived the tap: it came back on the next pointer move and the following tap
  // on open ground dropped a building nobody asked for.
  //
  // OUTSIDE the tool-change guard on purpose: arming a build while the right tool is
  // already in hand leaves selectedTool untouched, so gating the teardown on a tool
  // CHANGE meant tapping a tree with the axe already equipped kept the ghost alive.
  cancelPlacementForHand(c);
  if (c.selectedTool !== want) {
    c.selectedTool = want;
    if (c.tileFrame) c.tileFrame.visible = false;   // leaving hoe mode
    syncHeld(c);
    paintHotbar(c);
  }
}

// A tool/build click on the map: true = handled here (don't fall through to walking).

/**
 * Swing the equipped tool at a fence on this tile and remove it.
 *
 * Wooden fences answer to the axe, stone ones to the pickaxe — swinging the wrong
 * tool says so rather than silently doing nothing, because "nothing happened" is
 * indistinguishable from a bug. Returns true when the tap was claimed (including
 * the out-of-reach and wrong-tool cases, so the hero doesn't wander off instead).
 */
export function chopFenceAt(c: FarmCtx, tx: number, ty: number): boolean {
  // Look at the tapped tile first, then its immediate neighbours. A fence is a thin
  // rail drawn at the bottom of its tile, so a click that reads as "on the fence"
  // can land a tile off — demanding a pixel-perfect hit on a 16px rail is a good way
  // to make a working feature feel broken. Nearest match wins, so an unambiguous tap
  // still does exactly what you meant.
  const near: { b: NonNullable<ReturnType<typeof buildingAt>>; d: number }[] = [];
  for (let dy = -1; dy <= 1; dy++) {
    for (let dx = -1; dx <= 1; dx++) {
      const cand = buildingAt(c, tx + dx, ty + dy);
      if (cand && isFenceKind(cand.kind)) near.push({ b: cand, d: Math.abs(dx) + Math.abs(dy) });
    }
  }
  if (!near.length) return false;
  near.sort((a, z) => a.d - z.d);
  const b = near[0].b;
  tx = b.tx; ty = b.ty;

  const stone = b.kind.startsWith('fence_stone');
  const want = stone ? 'pickaxe' : 'axe';
  if (c.selectedTool !== want) {
    toast(stone ? 'A pickaxe would break that stone fence ⛏️' : 'An axe would cut that fence 🪓');
    return true;
  }
  if (!inToolReach(c, tx, ty)) {
    // Walk over and swing on arrival, the same as every other tool action.
    c.pendingPlot = { tx, ty };
    if (!walkTo(c, tx, ty)) c.player?.setTarget((tx + 0.5) * TILE, (ty + 0.85) * TILE);
    return true;
  }
  // Confirm first — the nearest-fence search below tolerates a click a whole tile
  // off, so "I meant the tile next to it" is a real way to lose a wall you paid for.
  confirmDemolish(b, () => {
    if (c.player) void c.player.playAction(stone ? 'mine' : 'chop', dirToTile(c, tx, ty));
    playSfx(stone ? 'pickaxe_hit' : 'axe_chop');
    demolish(c, tx, ty, b.kind, paintPlots);
  });
  return true;
}

// ─── Painting a RUN of tiles with one drag ───────────────────────────────────
// The rules live in paint-run.ts (pure, tested); this is the plumbing that feeds
// them pointer events, and it is the part jsdom cannot exercise.
//
// State is module-level rather than on FarmCtx because it belongs to a GESTURE, not
// to the farm: it is created on pointerdown and dropped on pointerup, and a scene
// rebuild in between must not resurrect it.
let paintRun: {
  kind: PaintKind;
  done: Set<string>;
  last: [number, number];
  count: number;
  /**
   * The ONE pointer this run belongs to.
   *
   * onPaintMove is bound to 'globalpointermove', which fires for every active
   * pointer. Without this, the second finger of a pinch-zoom (mobile is a
   * first-class target here) fed its position into strokeTiles against run.last and
   * Bresenham drew a line of roads straight across the map that the player never
   * touched. Moves and ups from any other pointer are now ignored, and a second
   * pointer going DOWN abandons the run outright — a pinch is not a paint.
   */
  pointerId: number;
} | null = null;

/** The tile under a pointer, in world px → tile coords. */
const tileOf = (wx: number, wy: number): [number, number] => [Math.floor(wx / TILE), Math.floor(wy / TILE)];

const worldPoint = (c: FarmCtx, e: FederatedPointerEvent): [number, number] | null => {
  const cam = c.cam;
  if (!cam) return null;
  const sc = cam.getScale();
  return [(e.global.x - cam.world.x) / sc, (e.global.y - cam.world.y) / sc];
};

/** The blocker the painted kind answers to — the SAME one the ghost and the tap use. */
const paintBlocker = (c: FarmCtx, kind: PaintKind) =>
  kind === 'build'
    // Materials on top of buildBlocker, which only answers map-shaped questions.
    // One tap could rely on the HUD greying out an unaffordable kind; a RUN cannot —
    // a drag longer than the wallet queued every tile and each came back a server
    // 400 with its own red toast. See affordBlocker for why `spent` is needed.
    ? (tx: number, ty: number) => {
        const def = BUILDINGS[c.buildMode ?? ''];
        const why = def && affordBlocker(def, c.bagCache?.resources ?? null, buildRunSpent + buildQueue.length);
        // A DRAG never destroys beds. The placement is allowed to (owner,
        // 2026-08-17) but only behind the confirm on a single tap — a run queues
        // tiles from a moving finger, and no dialog can honestly describe what a
        // stroke across a bed row is about to delete.
        const doomed = plotsDestroyedBy(c.farm?.plots ?? [], c.buildMode ?? '', tx, ty).length;
        return why ?? buildBlocker(c, tx, ty)
          ?? (doomed ? 'That would destroy a soil bed — place it with a single tap' : null);
      }
    // 'till' asks tillBlocker rather than hoeAction: a tile that already holds a bed
    // is not a refusal to explain, it is simply not part of a NEW run — hence the
    // explicit plotAt check, which keeps a drag over your own beds silent.
    : (tx: number, ty: number) => (plotAt(c, tx, ty) ? 'already-a-bed' : tillBlocker(c, tx, ty));

export const onPaintDown = (c: FarmCtx) => (e: FederatedPointerEvent) => {
  // A SECOND pointer landing while a run is live is a pinch, not a new paint: drop
  // the run and hand the gesture back to the camera. Re-arming here (what it did
  // before) meant the second finger of a zoom started painting from wherever it
  // touched down.
  if (paintRun) { endPaintRun(c); return; }
  const p = worldPoint(c, e);
  if (!p || !c.world) return;
  const [tx, ty] = tileOf(p[0], p[1]);
  const kind = paintStart({
    buildMode: c.buildMode,
    moving: !!c.moving,
    instant: (BUILDINGS[c.buildMode ?? '']?.buildMin ?? 0) === 0,
    selectedTool: c.selectedTool,
    hoeIntent: hoeIntent(),
    legal: c.buildMode ? buildBlocker(c, tx, ty) === null : hoeAction(c, tx, ty) === 'till',
  });
  if (!kind) return;                       // not a paint → the camera keeps the gesture
  paintRun = { kind, done: new Set(), last: [tx, ty], count: 0, pointerId: e.pointerId };
  buildRunSpent = 0;
  buildRunKind = c.buildMode;
};

export const onPaintMove = (c: FarmCtx) => (e: FederatedPointerEvent) => {
  const run = paintRun;
  if (!run || !c.cam) return;
  if (e.pointerId !== run.pointerId) return;   // another finger — see paintRun.pointerId
  const p = worldPoint(c, e);
  if (!p) return;
  const [tx, ty] = tileOf(p[0], p[1]);
  // The ANCHOR tile is painted here, not on pointerdown: a press that never moves is
  // an ordinary tap and must stay one (pointertap already handles it, including the
  // walk-to for an out-of-reach bed). Only once the finger has left its starting tile
  // is this unmistakably a run.
  const stroke = strokeTiles(run.last, [tx, ty], run.count === 0 && run.done.size === 0);
  run.last = [tx, ty];
  if (!stroke.length) return;

  const plan = planPaint(stroke, paintBlocker(c, run.kind), {
    done: run.done,
    budget: PAINT_GESTURE_MAX - run.count,
  });
  // Take the gesture off the camera only once something is actually being laid, so a
  // pan that happens to start on a buildable tile and immediately leaves it still pans.
  if (plan.place.length) {
    c.cam.panLocked = true;
    // Pixi will still fire a pointertap when the finger lifts, and `dragged` is the
    // flag onTap bails on — without this the last tile of every run got a second,
    // unrelated tap (a walk order, or a duplicate placement).
    c.cam.dragged = true;
    c.cam.dragMagnitude = Number.POSITIVE_INFINITY;
  }
  run.count += plan.place.length;
  for (const [px, py] of plan.place) {
    // QUEUED, never fired from here. Calling placeBuilding per tile inside the move
    // handler burst-fired one request per tile: the client funnel caps ALL actions
    // at 10/sec and then throws ActionDropped, which guard() swallows silently — so
    // a drag across more than ten tiles in a second lost every tile past the tenth,
    // with planPaint having already marked them done so nothing ever retried them.
    // pumpBuildQueue drains this at PAINT_PACE_MS and re-queues anything the funnel
    // still refuses.
    if (run.kind === 'build') buildQueue.push({ kind: c.buildMode!, tx: px, ty: py });
    else c.tillQueue.push({ tx: px, ty: py });
  }
  // One line per distinct reason, and notifyOnce keeps even that quiet after the
  // first time — dragging along a treeline must not stack thirty toasts.
  for (const why of plan.refusals) {
    if (why !== 'already-a-bed') notifyOnce(`paint:${why}`, why, 'error');
  }
  if (run.count >= PAINT_GESTURE_MAX) {
    notifyOnce('paint:budget', 'That is a long run — lift your finger and drag again', 'ok');
  }
};

export const onPaintUp = (c: FarmCtx) => (e?: FederatedPointerEvent) => {
  const run = paintRun;
  // A lift from the OTHER finger of a pinch must not end a run this pointer owns.
  if (run && e && e.pointerId !== run.pointerId) return;
  endPaintRun(c);
};

/** Tear down the current run and start whichever queue it filled. Shared by lift and pinch-abandon. */
function endPaintRun(c: FarmCtx): void {
  const run = paintRun;
  paintRun = null;
  if (c.cam) c.cam.panLocked = false;
  if (!run) return;
  if (run.kind === 'till' && c.tillQueue.length) {
    toast(`Tilling ${c.tillQueue.length} beds — walking the row 🌱`);
    pumpTillQueue(c);
  }
  // The build queue drains on the farm tick (pumpBuildQueue), which is what paces
  // it to PAINT_PACE_MS. Kick it once here so the first tile goes out on the lift
  // rather than waiting a frame.
  if (run.kind === 'build' && buildQueueLength()) {
    toast(`Placing ${buildQueueLength()} tiles 🧱`);
    pumpBuildQueue(c);
  }
}

/** How many painted placements are still waiting. The queue itself stays private. */
export const buildQueueLength = (): number => buildQueue.length;

// ─── The paced drain ─────────────────────────────────────────────────────────
// Both painted runs are QUEUES drained one item at a time, because every path to
// the server goes through the funnel in dispatch.ts, and the funnel DROPS rather
// than queues: 10 actions/sec globally, one in-flight call per type+key, and a
// 250ms debounce per slot. ActionDropped is swallowed by guard(), so a dropped tile
// used to be a tile that simply never existed and never said so.
//
// Both pumps take their dependencies as an argument with a live default, so the
// drain rule (pace, in-flight check, re-queue on drop) can be driven by a test —
// jsdom cannot run Pixi or the real API, but it can run this.

/** Tiles a build run still owes the server, in stroke order. */
let buildQueue: { kind: string; tx: number; ty: number }[] = [];
/** Earliest ms at which the next placement may be sent (see PAINT_PACE_MS). */
let buildNextAt = 0;
/** Tiles this run has already PAID for — the material projection, since the bag is re-read once at the end. */
let buildRunSpent = 0;
let buildRunKind: string | null = null;

export interface BuildPumpDeps {
  now: () => number;
  busy: (tx: number, ty: number) => boolean;
  /** Resolves 'ok' | 'dropped' | 'failed'. 'dropped' means the funnel refused — retry the tile. */
  send: (kind: string, tx: number, ty: number) => Promise<'ok' | 'dropped' | 'failed'>;
  /** Run once when the queue empties: one HUD/scene refresh and one toast per gesture, not per tile. */
  finish: () => void;
}

const liveBuildDeps = (c: FarmCtx): BuildPumpDeps => ({
  now: () => Date.now(),
  busy: (tx, ty) => isBusy('field.build', `${tx},${ty}`),
  send: (kind, tx, ty) => sendPaintedBuild(c, kind, tx, ty),
  finish: () => finishBuildRun(c),
});

/**
 * Send at most ONE queued placement, no faster than PAINT_PACE_MS. Called every frame.
 *
 * On a drop the tile goes back on the FRONT of the queue: it was never sent, and
 * the previous code's per-tile fire-and-forget had no way back — the tile was gone
 * from the plan and from the map.
 */
export function pumpBuildQueue(c: FarmCtx, deps: BuildPumpDeps = liveBuildDeps(c)): void {
  if (!buildQueue.length) return;
  const now = deps.now();
  if (now < buildNextAt) return;
  const t = buildQueue[0];
  if (deps.busy(t.tx, t.ty)) return;             // still in flight for this tile — do not consume it
  buildNextAt = now + PAINT_PACE_MS;
  buildQueue.shift();
  buildRunSpent += 1;
  void deps.send(t.kind, t.tx, t.ty).then((r) => {
    if (r === 'dropped') {
      // The funnel refused (global rate cap, or this tile is still in flight). Not a
      // failure — put it back and let the next tick try again.
      buildQueue.unshift(t);
      buildRunSpent -= 1;
      buildNextAt = deps.now() + PAINT_PACE_MS;
      return;
    }
    if (!buildQueue.length) deps.finish();
  });
}

/**
 * One tile of a painted run.
 *
 * Deliberately NOT placeBuilding: that was written for a single deliberate tap and
 * does a toast, a sound, an awaited refreshHud, refreshBag, a full collision rebuild
 * and a full plot repaint EVERY time. Forty of those in a run is forty toasts and
 * forty whole-scene rebuilds. Here the per-tile work is the request and the sound;
 * everything else happens once, in finishBuildRun.
 */
async function sendPaintedBuild(c: FarmCtx, kind: string, tx: number, ty: number): Promise<'ok' | 'dropped' | 'failed'> {
  try {
    setFarm(c, await dispatchAction('field.build',
      (actionId) => fieldApi.build(kind, tx, ty, actionId), { key: `${tx},${ty}` }));
    playSfx('build_place');
    return 'ok';
  } catch (e) {
    if (isDropped(e)) return 'dropped';
    // notifyOnce, not toast: a run the player cannot afford is one 400 per tile, and
    // toast() would stack a red banner for each of them.
    notifyOnce(`paint:build-error`, actionErrorMessage(e), 'error');
    return 'failed';
  }
}

/** The once-per-gesture tail of a build run: one refresh of everything, one summary line. */
function finishBuildRun(c: FarmCtx): void {
  const placed = buildRunSpent;
  const kind = buildRunKind;
  buildRunSpent = 0;
  buildRunKind = null;
  if (!placed || !kind) return;
  const name = BUILDINGS[kind]?.name ?? kind;
  toast(placed === 1 ? `${name} placed` : `${placed} × ${name} placed`);
  void (async () => {
    await c.game.refreshHud();
    refreshBag(c);
    // A new structure is a new obstacle: rebuild the grid so anyone mid-walk
    // re-routes around it instead of strolling through the wall.
    buildCollision(c);
    paintPlots(c);
    if (kind === 'forage_hut') mountVillagers(c);
  })();
}

/** Test seam: forget a gesture in flight and everything it queued. */
/** Test seam: queue painted placements without driving Pixi pointer events. */
export function __queueBuild(tiles: { kind: string; tx: number; ty: number }[]): void {
  buildQueue.push(...tiles);
}

export function __resetPaintRun(): void {
  paintRun = null;
  buildQueue = [];
  buildNextAt = 0;
  tillNextAt = 0;
  buildRunSpent = 0;
  buildRunKind = null;
}

/** Earliest ms at which the next till of a painted run may be sent. */
let tillNextAt = 0;

export interface TillPumpDeps {
  now: () => number;
  /** field.till goes through guard() with NO key — one funnel slot for every bed. */
  busy: () => boolean;
  /** Already a bed → nothing to do, drop the tile silently. */
  tilled: (tx: number, ty: number) => boolean;
  blocker: (tx: number, ty: number) => string | null;
  energy: () => boolean;
  fire: (tx: number, ty: number) => void;
  notify: (why: string) => void;
}

const liveTillDeps = (c: FarmCtx): TillPumpDeps => ({
  now: () => Date.now(),
  busy: () => isBusy('field.till'),
  tilled: (tx, ty) => !!plotAt(c, tx, ty),
  blocker: (tx, ty) => tillBlocker(c, tx, ty),
  energy: () => affordEnergy(c, 'till'),
  fire: (tx, ty) => requestPlot(c, tx, ty, 'hoe'),
  notify: (why) => notifyOnce(`paint:${why}`, why, 'error'),
});

/**
 * Start the next bed of a painted hoe run. Called on pointerup and again from the
 * farm tick every time the hero finishes (or gives up on) the previous one.
 *
 * WAS BROKEN: it shift()ed the tile off the queue unconditionally and called
 * requestPlot → actOnPlot, which tills through guard('field.till', …) with no key —
 * so one funnel slot, in-flight rejection and a 250ms debounce for the whole row.
 * The tick pumps again the moment pendingPlot clears, and the next bed is normally
 * already within reach, so the call fired while the previous till was still in
 * flight, ActionDropped was swallowed, and the bed was gone from the queue: a
 * painted row came out with holes in it and nothing said why. The tile is now only
 * consumed once the funnel will actually accept it.
 *
 * Tiles that stopped being tillable while he walked (someone built there, energy ran
 * out, the bed cap filled) are still dropped with their own words rather than silently.
 */
export function pumpTillQueue(c: FarmCtx, deps: TillPumpDeps = liveTillDeps(c)): void {
  if (!c.tillQueue.length) return;
  const now = deps.now();
  if (deps.busy() || now < tillNextAt) return;   // NOT consumed — the funnel would drop it
  while (c.tillQueue.length) {
    const t = c.tillQueue[0];
    if (deps.tilled(t.tx, t.ty)) { c.tillQueue.shift(); continue; }   // already tilled — nothing to say
    const why = deps.blocker(t.tx, t.ty);
    if (why) { c.tillQueue.shift(); deps.notify(why); continue; }
    // Out of energy stops the whole run: marching the hero down the row to fail at
    // every tile would be the same refusal twenty times over.
    if (!deps.energy()) { c.tillQueue.length = 0; return; }
    c.tillQueue.shift();
    // The debounce is per type+key and field.till has no key, so two beds inside
    // 250ms are the SAME slot and the second is dropped. Pace them apart.
    tillNextAt = now + PAINT_PACE_MS;
    deps.fire(t.tx, t.ty);
    return;
  }
}

export function toolTap(c: FarmCtx, wx: number, wy: number): boolean {
  if (!c.world) return false;
  const tx = Math.floor(wx / TILE), ty = Math.floor(wy / TILE);

  // Build mode: place (or set down) the selected structure on an eligible tile.
  if (c.buildMode) {
    const blocker = buildBlocker(c, tx, ty);
    if (blocker) {
      // Every refusal now says WHY. A red tile that swallows the click silently is
      // indistinguishable from a broken game, and "you are standing there" was the
      // only reason the player was ever told.
      toast(blocker);
      return true;                                // swallow — red tile, no walk-off
    }
    // The tent is not a Building row (it lives on the farm's tent_tx/tent_ty — see
    // the server's field/tent.ts), so it cannot go down the build endpoint. Same
    // ghost, same tile rules, different call.
    if (c.buildMode === TENT_KIND) { void pitchTent(c, tx, ty); return true; }
    // Beds the placement will DESTROY (owner, 2026-08-17). Asked here, before the
    // request, because the server deletes them without a second word: the loss must
    // never be a surprise — a planted bed is a paid-for seed and a grown crop.
    // Beds the placement will DESTROY (owner, 2026-08-17). Asked BEFORE the request,
    // because the server deletes them without a second word — the loss must never
    // be a surprise. The card is painted IN-GAME (plot-loss.ts) rather than
    // window.confirm(): a native dialog blocks the main thread, so the farm's
    // ticker stops dead behind it, and on a phone it lands as a system sheet over
    // a pixel-art game.
    //
    // It answers asynchronously, so the placement moved to the other side of it:
    // once asked, this tap is HANDLED and does nothing else.
    const kind = c.buildMode;
    if (!c.moving && plotsDestroyedBy(c.farm?.plots ?? [], kind, tx, ty).length) {
      confirmPlotLoss(c, kind, tx, ty, () => placeBuilding(c, kind, tx, ty));
      return true;
    }
    if (c.moving) moveBuilding(c, c.moving, tx, ty);
    else placeBuilding(c, kind, tx, ty);
    return true;
  }

  // Axe on a wooden fence, pickaxe on a stone one: tear it down. This is the way
  // out of a pen you fenced yourself into, and the reason fences are allowed to be
  // genuinely solid. The tool has to match the material — chopping a stone wall with
  // an axe should not work — and the tile has to be in reach like any other action.
  // Acorn armed: the tap plants, or says why it will not. Never falls through to a
  // walk — a swallowed tap on a red tile is indistinguishable from a broken game.
  if (isAcornArmed(c)) {
    const why = plantBlocker(c, tx, ty);
    if (why) { toast(why); return true; }
    if (inToolReach(c, tx, ty)) void plantAcornAt(c, tx, ty);
    else requestPlantAcorn(c, tx, ty);
    return true;
  }

  if ((c.selectedTool === 'axe' || c.selectedTool === 'pickaxe') && chopFenceAt(c, tx, ty)) return true;

  if (c.selectedTool !== 'hoe') return false;
  const p = plotAt(c, tx, ty);
  const intent = hoeIntent();
  const act = hoeAction(c, tx, ty, intent);
  // Alt-tap = "grass this bed over". Refusals say why instead of falling through
  // to a walk, same as every other hoe refusal — and the reason comes from the same
  // call the bracket colour did, so they cannot disagree.
  if (intent === 'clear') {
    if (act !== 'clear') { toast(tillBlocker(c, tx, ty, 'clear')!); return true; }
    if (inToolReach(c, tx, ty)) { c.pendingPlot = null; actOnPlot(c, tx, ty, 'hoe', 'clear'); }
    else requestPlot(c, tx, ty, 'hoe', 'clear');
    return true;
  }
  if (!act) {
    // §4b: a tree/stump on the tile is why it's not tillable — say so, and swallow
    // the click (red bracket already shows) so you don't wander onto a blocked tile.
    if (!p && treeOnTile(c, tx, ty)) { toast('Clear the tree first 🌳'); return true; }
    // Every OTHER refusal used to be silent — the tap fell through to a walk, so a
    // tile the hoe would not work on was indistinguishable from a broken hoe.
    if (!p) {
      const why = tillBlocker(c, tx, ty);
      if (why) { toast(why); return true; }        // swallow: red bracket already shown
    }
    // Existing bed the hoe can't till/harvest (dry, or wet-but-unseeded) → still
    // route to the plot action so it WATERS or SEEDS it. Before this, holding the
    // hoe swallowed the click and watering "didn't work" until you deselected it.
    if (p) {
      if (inToolReach(c, tx, ty)) { c.pendingPlot = null; actOnPlot(c, tx, ty, 'hoe'); }
      else requestPlot(c, tx, ty, 'hoe');
      return true;
    }
    return false;                                  // bare non-tillable ground → normal walk
  }
  c.pendingAction = null;
  if (inToolReach(c, tx, ty)) { c.pendingPlot = null; actOnPlot(c, tx, ty, 'hoe'); }
  else requestPlot(c, tx, ty, 'hoe');
  return true;
}

/** Place a building (walk-to not required — it drops where you tapped). */
export function placeBuilding(c: FarmCtx, kind: string, tx: number, ty: number) {
  // Keyed per TILE: the funnel debounces 250ms and drops while a same-key call is in
  // flight, so an unkeyed build silently swallowed most of a fence run laid at normal
  // click speed (~6/s) — pieces just never appeared, with no error. Each tile is its
  // own target, exactly like a plot's harvest; the real placement ceiling is the
  // server's per-farm limiter, and a repeat tap on the SAME tile is still debounced.
  void guard('field.build', async (actionId) => {
    setFarm(c, await fieldApi.build(kind, tx, ty, actionId));
    playSfx('build_place');
    // Say WHICH it is: a timed kind lands as scaffolding, and "placed" alone reads
    // as "done" to a player watching a building that clearly is not.
    const mins = BUILDINGS[kind]?.buildMin ?? 0;
    const name = BUILDINGS[kind]?.name ?? kind;
    toast(mins > 0 ? `${name} under construction — ${mins} min` : `${name} placed`);
    await c.game.refreshHud();
      refreshBag(c);
    // A new structure is a new obstacle: rebuild the grid so anyone mid-walk
    // re-routes around it instead of strolling through the wall.
    buildCollision(c);
    paintPlots(c);
    // A Forager Hut comes with a keeper standing at it — put him there now rather
    // than at the next scene rebuild, or the shop you just built looks unstaffed.
    if (kind === 'forage_hut') mountVillagers(c);
    // ── DISARM after placing anything that TAKES TIME ────────────────────────
    // Build mode used to stay armed after every placement, which is right for
    // fences and roads (laid in runs at ~6 clicks/s) and wrong for everything
    // else. A structure lands, the ghost stays stuck to the cursor, and from that
    // moment EVERY tap on a building belongs to placement: the sprite handler
    // hands the click to toolTap, which answers "Something is already built
    // there". That is one bug wearing three faces — the market not opening, the
    // crafting table not opening, and the scaffolding you just placed refusing to
    // show its Finish-now panel. Nothing on screen says build mode is still on,
    // and the only exits are Escape or the Build button.
    //
    // Timed kinds also cannot be placed back-to-back (one build at a time), so
    // keeping the ghost armed after one offers the player nothing but refusals.
    if ((BUILDINGS[kind]?.buildMin ?? 0) > 0) { exitBuildMode(c); paintChrome(c); }
  }, `${tx},${ty}`);
}

/**
 * Set a picked-up building down on a new tile.
 *
 * Server-authoritative and deliberately NOT unbuild+build: that pair would charge
 * the materials again (unbuild refunds nothing), reset a running build timer, and
 * spend two slots of the placement budget instead of one. `field/move` moves the
 * row and nothing else — see FieldService.moveBuilding.
 */
export function moveBuilding(c: FarmCtx, from: { tx: number; ty: number; kind: string }, tx: number, ty: number) {
  // NOT `guard` + an unconditional exit: the funnel DROPS a same-key call that is
  // already in flight and says nothing, so a double-tap used to tear placement down
  // while the building had not moved — the ghost vanished and read as "moved".
  // Teardown now happens only on a request that actually reached the server.
  void (async () => {
    try {
      setFarm(c, await dispatchAction('field.move',
        (actionId) => fieldApi.move(from.tx, from.ty, tx, ty, actionId), { key: `${tx},${ty}` }));
    } catch (e) {
      if (isDropped(e)) return;                     // still armed; the first tap is landing
      exitBuildMode(c); paintChrome(c);             // a real refusal ends the placement
      toast(actionErrorMessage(e), 'error');
      return;
    }
    playSfx('build_place');
    toast(`${BUILDINGS[from.kind]?.name ?? from.kind} moved`);
    // A move both frees and blocks tiles, so the grid has to be rebuilt before
    // anyone mid-walk plans a route through where the barn used to be.
    // Placement is over: leaving the ghost armed after a commit invites a second
    // "move" of a building that is no longer where the ghost thinks it is.
    exitBuildMode(c);
    buildCollision(c);
    paintPlots(c);
    paintChrome(c);          // the Build button drops out of its "Building…" state
  })();
}

/**
 * Pitch the packed tent on a tile.
 *
 * The server has the final say on whether the spot is legal — the client's tile
 * rules are a courtesy so the ghost turns red early, not the authority — so the
 * refusal is simply reported rather than second-guessed. On success the returned
 * status REPLACES `c.tent`, because that is what the mob loop reads to decide
 * whether the hero is safe; leaving a stale one there would draw a safe zone the
 * mobs do not honour.
 */
async function pitchTent(c: FarmCtx, tx: number, ty: number) {
  try {
    c.tent = await tentApi.place(tx, ty);
    exitBuildMode(c);
    paintChrome(c);
    await c.load();                     // redraw the world with the tent in its new spot
    toast('Tent pitched — you are safe here');
  } catch (e) {
    toast(actionErrorMessage(e), 'error');
  }
}

/**
 * Leave placement mode — the single writer, so the ghost, the tile bracket, the
 * move state and the HUD's Build button can never end up disagreeing about whether
 * something is armed.
 */
export function exitBuildMode(c: FarmCtx) {
  c.buildMode = null;
  c.moving = null;
  if (c.tileFrame) c.tileFrame.visible = false;
  hideBuildGhost(c);
}

/**
 * The one door out of placement mode taken by anything that puts something ELSE in
 * the hero's hands — a hotbar tool, the torch, the acorn key.
 *
 * Each of those wrote `selectedTool` and nothing else, so `buildMode` stayed armed:
 * updateToolFrame paints the ghost purely from `c.buildMode`, so the red building
 * came straight back on the next pointer move, and the tap router above still sent
 * every map tap into the build branch — the newly picked pickaxe was inert. One
 * shared exit, so a new hand-swapping affordance cannot forget it.
 *
 * Cancelling a MOVE is deliberate: `moving` is part of the same armed state, and a
 * relocation you have walked away from with an axe out is over.
 */
export function cancelPlacementForHand(c: FarmCtx, silent = false): boolean {
  if (!c.buildMode) return false;
  const wasMove = !!c.moving;
  exitBuildMode(c);
  paintChrome(c);           // the Build button is armed chrome — every exit repaints it
  // `silent` is for callers that say their own thing in the same keystroke — the
  // acorn key already announces what went INTO the hand, and two toasts for one
  // press read as two events (dom.ts only dedupes identical text).
  if (!silent) toast(wasMove ? 'Move cancelled' : 'Placement cancelled');
  return true;
}
